import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users,
  Building2,
  CreditCard,
  KeyRound,
  Bell,
  Mail,
  Plug,
  ScrollText,
  ArrowRight,
  Lock,
  Sparkles,
  Boxes,
  Compass, Tags, ShieldCheck, Scale, Briefcase } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useIsAccessManager } from '@/lib/permissions/useCanAccess';
import { useHasFeature } from '@/lib/permissions/useCompanyFeatures';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { useTourStore } from '@/lib/tour/useTourStore';
import { toast } from '@/components/ui/Toast';

/** Plan features that gate a settings row behind the upgrade dialog. */
type PaywallFeature = 'notifications' | 'activeLog';

interface Section {
  icon: React.ReactNode;
  label: string;
  description: string;
  to?: string;
  comingSoon?: boolean;
  hidden?: boolean;
  /** Plan feature this row needs; when the plan lacks it, clicking opens the upgrade dialog. */
  feature?: PaywallFeature;
  /** Computed from `feature` — the plan does not include it. */
  locked?: boolean;
}

function useSections(): Section[] {
  const isAccessManager = useIsAccessManager();
  const hasNotifications = useHasFeature('notifications');
  const hasActiveLog = useHasFeature('activeLog');
  const t = useT();
  return [
    {
      icon: <Building2 className="h-5 w-5" />,
      label: t('settings.section.account'),
      description: t('settings.section.accountDesc'),
      to: '/profile',
    },
    {
      icon: <Briefcase className="h-5 w-5" />,
      label: t('settings.section.workspaceType'),
      description: t('settings.section.workspaceDesc'),
      to: '/settings/workspace',
    },
    {
      icon: <Users className="h-5 w-5" />,
      label: t('settings.section.access'),
      description: t('settings.section.accessDesc'),
      to: '/settings/access-manager',
      hidden: !isAccessManager,
    },
    {
      icon: <Boxes className="h-5 w-5" />,
      label: t('settings.section.brand'),
      description: t('settings.section.brandDesc'),
      to: '/settings/brand',
    },
    {
      icon: <Scale className="h-5 w-5" />,
      label: t('settings.section.legal'),
      description: t('settings.section.legalDesc'),
      to: '/settings/legal',
    },
    {
      icon: <ShieldCheck className="h-5 w-5" />,
      label: t('sessions.title'),
      description: t('sessions.settingsDesc'),
      to: '/settings/sessions',
    },
    {
      icon: <Tags className="h-5 w-5" />,
      label: t('tags.settingsSection'),
      description: t('tags.settingsSectionDesc'),
      to: '/settings/tags',
    },
    {
      icon: <Bell className="h-5 w-5" />,
      label: t('settings.section.notifications'),
      description: t('settings.section.notificationsDesc'),
      to: '/notifications/compose',
      feature: 'notifications',
      locked: !hasNotifications,
    },
    {
      icon: <ScrollText className="h-5 w-5" />,
      label: t('settings.section.activeLog'),
      description: t('settings.section.activeLogDesc'),
      to: '/active-log',
      feature: 'activeLog',
      locked: !hasActiveLog,
    },
    {
      icon: <CreditCard className="h-5 w-5" />,
      label: t('settings.section.billing'),
      description: t('settings.section.billingDesc'),
      to: '/settings/billing',
    },
    {
      icon: <Mail className="h-5 w-5" />,
      label: t('settings.section.followup'),
      description: t('settings.section.followupDesc'),
      to: '/settings/followup',
    },
    {
      icon: <Plug className="h-5 w-5" />,
      label: t('settings.section.integrations'),
      description: t('settings.section.integrationsDesc'),
      to: '/settings/integrations',
    },
    {
      icon: <KeyRound className="h-5 w-5" />,
      label: t('settings.section.api'),
      description: t('settings.section.apiDesc'),
      comingSoon: true,
    },
  ];
}

export function SettingsPage() {
  const sections = useSections().filter((s) => !s.hidden);
  const t = useT();
  const resetTours = useTourStore((s) => s.resetAll);
  const [paywallFeature, setPaywallFeature] = useState<PaywallFeature | null>(null);
  useFavoritable(t('sidebar.settings'));
  return (
    <div className="page space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          {t('settings.title')}
          <HelpHint section="settings-overview" />
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('settings.subtitle')}
        </p>
      </header>

      <Card>
        <CardContent className="p-0">
          <ul className="divide-y divide-[var(--color-border-subtle)]">
            {sections.map((s) => (
              <li key={s.label}>
                {s.comingSoon ? (
                  <div className="flex items-center gap-4 px-6 py-4 opacity-60">
                    <SectionContent section={s} />
                  </div>
                ) : s.locked ? (
                  <button
                    type="button"
                    onClick={() => s.feature && setPaywallFeature(s.feature)}
                    className="flex w-full items-center gap-4 px-6 py-4 text-left transition-colors hover:bg-[var(--color-bg-elevated)]"
                  >
                    <SectionContent section={s} />
                  </button>
                ) : s.to ? (
                  <Link
                    to={s.to}
                    className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-[var(--color-bg-elevated)]"
                  >
                    <SectionContent section={s} />
                  </Link>
                ) : (
                  <div className="flex items-center gap-4 px-6 py-4">
                    <SectionContent section={s} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Product tours — replay the guided walkthroughs from scratch. */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--color-accent-10)] text-[var(--color-accent-ink)]">
              <Compass className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-bold text-[var(--color-fg-primary)]">
                {t('tour.restart')}
              </p>
              <p className="text-xs text-[var(--color-fg-muted)]">{t('tour.restartHint')}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              resetTours();
              toast({ tone: 'success', title: t('tour.restartDone') });
            }}
          >
            {t('tour.restart')}
          </Button>
        </CardContent>
      </Card>

      {paywallFeature && (
        <FeaturePaywallDialog
          feature={paywallFeature}
          open
          onOpenChange={(o) => {
            if (!o) setPaywallFeature(null);
          }}
        />
      )}
    </div>
  );
}

function SectionContent({ section }: { section: Section }) {
  const t = useT();
  return (
    <>
      <span className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-[var(--color-bg-elevated)] text-[var(--color-accent-ink)]">
        {section.icon}
      </span>
      <div className="flex-1">
        <p className="flex items-center gap-2 font-bold">
          {section.label}
          {section.comingSoon && <Badge tone="warn">{t('settings.comingSoon')}</Badge>}
        </p>
        <p className="text-sm text-[var(--color-fg-muted)]">{section.description}</p>
      </div>
      {section.locked ? (
        <Lock className="h-4 w-4 text-[var(--color-fg-subtle)]" />
      ) : !section.comingSoon ? (
        <ArrowRight className="h-4 w-4 text-[var(--color-fg-subtle)]" />
      ) : null}
    </>
  );
}

/**
 * Upgrade prompt shown when a plan without the given feature clicks a gated
 * settings row. Mirrors the AI Assistant paywall messaging — content comes
 * from the shared `paywall.<feature>.*` translation keys.
 */
function FeaturePaywallDialog({
  feature,
  open,
  onOpenChange,
}: {
  feature: PaywallFeature;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const t = useT();
  const perks = [
    t(`paywall.${feature}.perk1`),
    t(`paywall.${feature}.perk2`),
    t(`paywall.${feature}.perk3`),
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="mb-4 grid h-12 w-12 place-items-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]">
            <Sparkles className="h-5 w-5" />
          </div>
          <DialogTitle>{t(`paywall.${feature}.title`)}</DialogTitle>
          <DialogDescription>
            {t(`paywall.${feature}.description`)}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm text-[var(--color-fg-muted)]">
          {perks.map((p) => (
            <li key={p} className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-[var(--color-accent)]"
              />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('upgradeDialog.maybeLater')}
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              navigate('/pricing');
            }}
          >
            {t('paywall.cta.viewPlans')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
